import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Logo } from "./Logo";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-forest/10 bg-cream/85 backdrop-blur-xl" data-testid="site-navbar">
      <div className="mx-auto flex h-28 max-w-7xl items-center justify-between px-6 lg:px-10">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link" onClick={() => setOpen(false)}>
          <Logo size={80} />
          <span className="leading-none">
            <span className="block font-display text-3xl font-semibold tracking-wide text-forest">ROOTED</span>
            <span className="mt-1 block text-[11px] font-semibold uppercase tracking-[0.32em] text-sage">
              Business Advisors
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-9 lg:flex" aria-label="Primary">
          {LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={`link-underline text-[11px] font-semibold uppercase tracking-[0.22em] transition-colors duration-300 ${
                pathname === link.to ? "text-forest" : "text-sage hover:text-forest"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="group flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-gold hover:text-forest"
          >
            Request a Consultation
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <button
          className="text-forest lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-forest/10 bg-cream lg:hidden"
            aria-label="Mobile"
          >
            <div className="flex flex-col gap-5 px-6 py-8">
              {LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  className="font-display text-2xl text-forest"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-cta-button"
                className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-forest px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-cream"
              >
                Request a Consultation
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
